import { Fragment, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite } from '../lib/api'
import { useAuth } from '../auth'
import { Empty, PageHead, Skel, Stat } from '../components/ui'
import { number, thaiDateTime } from '../lib/format'

/**
 * Guarded AI draft assist (parts 18/41/70).
 *
 * This page can generate a draft, read it, and record a human accept/reject decision. It has no
 * control that changes a finding. The backend does not expose one here either: accepting a draft
 * says the wording is usable, not that the finding is approved.
 *
 * A draft whose model is "template" was built deterministically in Go because no provider was
 * configured. It is labelled as a template everywhere; the UI never presents it as model output.
 */

type DraftStatus = 'draft' | 'accepted' | 'rejected'
type Decision = 'accepted' | 'rejected'

interface AIDraftRow {
  id: string
  finding_id: string
  finding_title?: string
  model: string
  prompt_tokens: number
  completion_tokens: number
  draft: string
  status: DraftStatus
  reviewed_by?: string
  reviewed_by_name?: string
  reviewed_at?: string
  created_at: string
}

interface FindingOption {
  id: string
  rule_code: string
  title: string
  severity: string
  status: string
  amount_at_risk_satang: number
}

const statusLabel: Record<DraftStatus, string> = {
  draft: 'รอตรวจ',
  accepted: 'ยอมรับแล้ว',
  rejected: 'ตีกลับแล้ว',
}

const severityLabel: Record<string, string> = {
  low: 'ต่ำ', medium: 'ปานกลาง', high: 'สูง', critical: 'วิกฤต',
}

function shortID(id: string): string {
  return id.length > 12 ? `${id.slice(0, 8)}…` : id
}

export function AIDrafts() {
  const { me } = useAuth()
  const qc = useQueryClient()
  const canDraft = me?.role === 'owner' || me?.role === 'admin' || me?.role === 'audit_manager' || me?.role === 'auditor'
  const canReview = me?.role === 'owner' || me?.role === 'admin' || me?.role === 'audit_manager' || me?.role === 'reviewer'

  const [status, setStatus] = useState<DraftStatus | 'all'>('all')
  const [findingId, setFindingId] = useState('')
  const [openId, setOpenId] = useState<string | null>(null)
  const [confirm, setConfirm] = useState<{ id: string; decision: Decision } | null>(null)
  const [notice, setNotice] = useState('')

  const allDrafts = useQuery({
    queryKey: ['ai-drafts', 'all'],
    queryFn: () => api<AIDraftRow[]>('/v1/ai-drafts'),
    staleTime: 10_000,
  })
  const drafts = useQuery({
    queryKey: ['ai-drafts', status],
    queryFn: () => api<AIDraftRow[]>(`/v1/ai-drafts${status === 'all' ? '' : `?status=${status}`}`),
    enabled: status !== 'all',
    staleTime: 10_000,
  })
  const findings = useQuery({
    queryKey: ['findings', 'ai-draft-options'],
    queryFn: () => api<FindingOption[]>('/v1/findings?limit=200'),
    staleTime: 30_000,
  })

  const generate = useMutation({
    mutationFn: () => apiWrite<AIDraftRow>(`/v1/findings/${findingId}/ai-drafts`, 'POST', {}),
    onSuccess: (row) => {
      setOpenId(row.id)
      setNotice(row.model === 'template'
        ? 'สร้างฉบับร่างจากแม่แบบแล้ว — ระบบไม่ได้เรียกโมเดลภายนอก'
        : `สร้างฉบับร่างด้วย ${row.model} แล้ว — ต้องตรวจทานก่อนใช้ทุกครั้ง`)
      void qc.invalidateQueries({ queryKey: ['ai-drafts'] })
    },
    onError: () => setNotice(''),
  })

  const review = useMutation({
    mutationFn: (input: { id: string; decision: Decision }) =>
      apiWrite<AIDraftRow>(`/v1/ai-drafts/${input.id}/review`, 'POST', { decision: input.decision }),
    onSuccess: (row) => {
      setConfirm(null)
      setOpenId(row.id)
      setNotice(row.status === 'accepted'
        ? 'บันทึกการยอมรับฉบับร่างแล้ว — สถานะข้อสังเกตไม่เปลี่ยนแปลง'
        : 'บันทึกการตีกลับฉบับร่างแล้ว — ระบบไม่แก้ไขข้อสังเกตให้')
      void qc.invalidateQueries({ queryKey: ['ai-drafts'] })
    },
  })

  const rows = status === 'all' ? (allDrafts.data ?? []) : (drafts.data ?? [])
  const options = useMemo(
    () => [...(findings.data ?? [])].sort((a, b) => b.amount_at_risk_satang - a.amount_at_risk_satang),
    [findings.data],
  )
  const selectedFinding = options.find((f) => f.id === findingId)

  const counts = useMemo(() => {
    const out = { draft: 0, accepted: 0, rejected: 0 }
    for (const row of allDrafts.data ?? []) out[row.status] += 1
    return out
  }, [allDrafts.data])

  const loading = allDrafts.isLoading || (status !== 'all' && drafts.isLoading)
  const loadError = allDrafts.error ?? drafts.error
  const refreshing = allDrafts.isFetching || (status !== 'all' && drafts.isFetching)
  const refetchAll = () => {
    void allDrafts.refetch()
    if (status !== 'all') void drafts.refetch()
  }

  return (
    <>
      <PageHead
        title="ฉบับร่างคำอธิบายโดย AI"
        sub="AI ช่วยร่างถ้อยคำจากข้อสังเกตที่กฎตัดสินแล้ว — มนุษย์เป็นผู้อ่านและผู้ตัดสิน"
        actions={
          <button className="btn btn-ghost btn-sm" onClick={refetchAll} disabled={refreshing}>
            {refreshing ? 'กำลังโหลด…' : 'รีเฟรช'}
          </button>
        }
      />

      {/* The banner is the page's contract with the reviewer; it stays above every action. */}
      <div
        className="card card-pad"
        style={{
          marginBottom: 18,
          borderColor: 'rgba(200, 169, 106, 0.35)',
          background: 'linear-gradient(180deg, rgba(200, 169, 106, 0.07), transparent)',
        }}
      >
        <div className="row" style={{ alignItems: 'flex-start', gap: 12 }}>
          <span className="chip gold">การ์ดเรล</span>
          <div>
            <div style={{ fontSize: 13.5, lineHeight: 1.75 }}>
              AI เป็นเพียงผู้ช่วยร่างคำอธิบาย ไม่มีสิทธิ์ตัดสินข้อสังเกต และไม่สามารถเปลี่ยนสถานะใด ๆ ของข้อสังเกตได้
            </div>
            <div className="dim" style={{ fontSize: 12, lineHeight: 1.75, marginTop: 6 }}>
              ทุกฉบับร่างต้องผ่านการอ่านและตัดสินใจโดยมนุษย์ การยอมรับในหน้านี้เป็นการรับรองถ้อยคำเท่านั้น
              ไม่ใช่การอนุมัติผลการตรวจสอบ
            </div>
          </div>
        </div>
      </div>

      <div className="grid-4" style={{ marginBottom: 20 }}>
        <Stat label="ฉบับร่างทั้งหมด" value={rows.length} unit="รายการ" foot="ตามตัวกรองที่เลือก" />
        <Stat label="รอตรวจ" value={counts.draft} unit="รายการ" foot="ยังไม่มีผู้ลงชื่อ" />
        <Stat label="ยอมรับแล้ว" value={counts.accepted} unit="รายการ" foot="ถ้อยคำผ่านการตรวจ" />
        <Stat label="ตีกลับแล้ว" value={counts.rejected} unit="รายการ" foot="ไม่นำถ้อยคำไปใช้" />
      </div>

      <div className="card card-pad" style={{ marginBottom: 20 }}>
        <div className="card-label">สร้างฉบับร่างใหม่</div>
        <p className="dim" style={{ fontSize: 12, lineHeight: 1.8, marginTop: 10 }}>
          เลือกข้อสังเกตที่กฎตรวจพบ ระบบจะสร้างฉบับร่างให้อ่านและตัดสิน
          หากยังไม่ได้ตั้งค่าผู้ให้บริการ AI ระบบจะสร้างจากแม่แบบในเครื่องและระบุว่าเป็นแม่แบบอย่างชัดเจน
        </p>

        <div className="row" style={{ gap: 10, marginTop: 14, flexWrap: 'wrap' }}>
          <label htmlFor="ai-draft-finding" className="dim" style={{ fontSize: 12 }}>ข้อสังเกต</label>
          <select
            id="ai-draft-finding"
            className="select"
            style={{ minWidth: 360, maxWidth: '100%' }}
            value={findingId}
            onChange={(e) => { setFindingId(e.target.value); setNotice('') }}
            disabled={!canDraft || findings.isLoading}
          >
            <option value="">— เลือกข้อสังเกต —</option>
            {options.map((f) => (
              <option key={f.id} value={f.id}>
                {f.title} · {f.rule_code} · {severityLabel[f.severity] ?? f.severity}
              </option>
            ))}
          </select>
          <button
            className="btn btn-primary"
            disabled={!canDraft || findingId === '' || generate.isPending}
            onClick={() => generate.mutate()}
          >
            {generate.isPending ? 'กำลังสร้างฉบับร่าง…' : 'สร้างฉบับร่าง'}
          </button>
        </div>

        {!canDraft ? (
          <div className="dim" style={{ fontSize: 11.5, marginTop: 10 }}>
            บทบาทของคุณอ่านฉบับร่างได้ แต่ไม่มีสิทธิ์สร้างฉบับร่างใหม่
          </div>
        ) : null}

        {selectedFinding ? (
          <div className="dim" style={{ fontSize: 11.5, marginTop: 10, lineHeight: 1.7 }}>
            รหัสอ้างอิง {shortID(selectedFinding.id)} · สถานะข้อสังเกต {selectedFinding.status} ·
            มูลค่าความเสี่ยง {(selectedFinding.amount_at_risk_satang / 100).toLocaleString('th-TH', { minimumFractionDigits: 2 })} บาท
          </div>
        ) : null}

        {generate.isError ? (
          <div className="form-error">
            {generate.error instanceof Error ? generate.error.message : 'สร้างฉบับร่างไม่สำเร็จ'}
          </div>
        ) : null}

        {notice ? (
          <div className="dim" style={{ fontSize: 12, marginTop: 12, color: 'var(--color-gold)', lineHeight: 1.7 }}>
            {notice}
          </div>
        ) : null}
      </div>

      <div className="filters">
        <label htmlFor="ai-draft-status" className="dim" style={{ fontSize: 12 }}>สถานะ</label>
        <select
          id="ai-draft-status"
          className="select"
          value={status}
          onChange={(e) => setStatus(e.target.value as DraftStatus | 'all')}
        >
          <option value="all">ทั้งหมด</option>
          <option value="draft">รอตรวจ</option>
          <option value="accepted">ยอมรับแล้ว</option>
          <option value="rejected">ตีกลับแล้ว</option>
        </select>
        <span className="chip" style={{ marginLeft: 'auto' }}>{rows.length} รายการ</span>
      </div>

      {loading ? <Skel h={220} /> : loadError ? (
        <div className="form-error">
          {loadError instanceof Error ? loadError.message : 'โหลดฉบับร่างไม่สำเร็จ'}
        </div>
      ) : rows.length === 0 ? (
        <Empty
          title="ยังไม่มีฉบับร่าง"
          body="เลือกข้อสังเกตด้านบนแล้วกด “สร้างฉบับร่าง” ฉบับร่างจะยังไม่ถูกนำไปใช้จนกว่ามนุษย์จะยอมรับ"
        />
      ) : (
        <div className="table-wrap">
          <table className="table data">
            <thead>
              <tr>
                <th>สร้างเมื่อ</th>
                <th>ข้อสังเกต</th>
                <th>โมเดล</th>
                <th className="num">โทเคน</th>
                <th>สถานะ</th>
                <th>ผู้ตรวจ</th>
                <th>ฉบับร่าง</th>
                <th style={{ textAlign: 'right' }}>การตัดสิน</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const isOpen = openId === row.id
                const deciding = confirm?.id === row.id
                return (
                  <Fragment key={row.id}>
                    <tr style={{ cursor: 'default' }}>
                      <td className="dim" style={{ whiteSpace: 'nowrap' }}>{thaiDateTime(row.created_at)}</td>
                      <td>
                        <div>{row.finding_title || 'ไม่พบชื่อข้อสังเกต'}</div>
                        <div className="dim mono" style={{ fontSize: 10.5, marginTop: 3 }}>{shortID(row.finding_id)}</div>
                      </td>
                      <td>
                        {row.model === 'template' ? (
                          <span className="chip" title="สร้างจากแม่แบบในเครื่อง ไม่ได้เรียกโมเดล">
                            แม่แบบในระบบ
                          </span>
                        ) : (
                          <span className="chip gold" title="สร้างโดยผู้ให้บริการโมเดลที่ตั้งค่าไว้">
                            {row.model}
                          </span>
                        )}
                      </td>
                      <td className="num">
                        <div>{number(row.prompt_tokens + row.completion_tokens)}</div>
                        <div className="dim tnum" style={{ fontSize: 10.5, marginTop: 3 }}>
                          {number(row.prompt_tokens)} / {number(row.completion_tokens)}
                        </div>
                      </td>
                      <td>
                        <span
                          className="chip"
                          style={row.status === 'accepted' ? { color: 'var(--color-gold)', borderColor: 'rgba(200,169,106,0.35)' } : row.status === 'rejected' ? { color: '#E3A0A9' } : undefined}
                        >
                          {statusLabel[row.status]}
                        </span>
                      </td>
                      <td>
                        {row.reviewed_at ? (
                          <>
                            <div>{row.reviewed_by_name || 'ผู้ใช้ที่ถูกลบ'}</div>
                            <div className="dim" style={{ fontSize: 10.5, marginTop: 3 }}>{thaiDateTime(row.reviewed_at)}</div>
                          </>
                        ) : (
                          <span className="dim" style={{ fontSize: 11.5 }}>ยังไม่ตรวจ</span>
                        )}
                      </td>
                      <td>
                        <button className="btn btn-ghost btn-sm" onClick={() => setOpenId(isOpen ? null : row.id)}>
                          {isOpen ? 'ซ่อน' : 'อ่าน'}
                        </button>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {row.status !== 'draft' ? (
                          <span className="dim" style={{ fontSize: 11.5 }}>ตัดสินแล้ว</span>
                        ) : !canReview ? (
                          <span className="dim" style={{ fontSize: 11.5 }}>รอผู้มีสิทธิ์ตรวจ</span>
                        ) : deciding ? (
                          <span className="row" style={{ gap: 6, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                            <span className="dim" style={{ fontSize: 11 }}>
                              {confirm?.decision === 'accepted' ? 'ยืนยันการยอมรับ?' : 'ยืนยันการตีกลับ?'}
                            </span>
                            <button
                              className="btn btn-primary btn-sm"
                              disabled={review.isPending}
                              onClick={() => confirm && review.mutate(confirm)}
                            >
                              {review.isPending ? 'กำลังบันทึก…' : 'ยืนยัน'}
                            </button>
                            <button className="btn btn-ghost btn-sm" onClick={() => setConfirm(null)}>ยกเลิก</button>
                          </span>
                        ) : (
                          <span className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={() => setConfirm({ id: row.id, decision: 'accepted' })}
                            >
                              ยอมรับ
                            </button>
                            <button
                              className="btn btn-ghost btn-sm"
                              style={{ color: '#E3A0A9' }}
                              onClick={() => setConfirm({ id: row.id, decision: 'rejected' })}
                            >
                              ตีกลับ
                            </button>
                          </span>
                        )}
                      </td>
                    </tr>
                    {isOpen ? (
                      <tr>
                        <td colSpan={8} style={{ background: 'var(--color-wash)', cursor: 'default' }}>
                          <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.85, fontSize: 13 }}>
                            {row.draft}
                          </div>
                          <div className="dim" style={{ fontSize: 11.5, lineHeight: 1.7, marginTop: 12, borderTop: '1px solid var(--color-hairline)', paddingTop: 10 }}>
                            ฉบับร่างนี้ไม่มีผลต่อสถานะข้อสังเกต
                            การเปลี่ยนสถานะต้องทำในหน้าข้อสังเกตตามขั้นตอนอนุมัติปกติ
                          </div>
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {review.isError ? (
        <div className="form-error">
          {review.error instanceof Error ? review.error.message : 'บันทึกการตัดสินไม่สำเร็จ'}
        </div>
      ) : null}
    </>
  )
}
